import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { SectionLabel, SourceBars } from "@/components/ui-bits";
import { Plus, X, Clock, CheckCircle2, ClipboardList, TrendingUp, Sparkles, Flag, Target, GitBranch, Archive, Check as CheckIcon } from "lucide-react";
import "@/components/meos-extra.css";

const LIFECYCLE = ["Idea", "Advisor Recommendation", "Strategic Decision", "Approved", "Implementation", "Monitoring", "Review", "Completed", "Knowledge Archive"];
const CATEGORIES = ["General", "Partnerships", "Product", "Marketing", "Pricing", "Operations", "Finance", "Hiring", "Growth"];
const AREAS = ["General", "Growth", "Funnel", "Revenue", "Marketing", "Partnerships", "Product", "Customer Success", "Email"];

const stClass = (s) => `status-pill st-${(s || "Idea").replace(/[\s/]/g, "")}`;

function emptyForm() {
  return {
    title: "", category: "General", business_area: "General", owner: "Angella", status: "Idea",
    source: "Manual", recommendation_id: "", confidence: "", priority: "", recommended_timeline: "", related_eii: "",
    recommendation_summary: "", related_kpis: "",
    challenge: "", opportunity: "", goals: "", assumptions: "", risks: "", alternatives: "", rationale: "",
    action_plan: "", budget: "", timeline: "", milestones: "", dependencies: "",
    expected_kpi: "", expected_target: "", target_results: "", review_date: "", success_criteria: "",
    actual_results: "", revenue_impact: "", lessons_learned: "", unexpected_outcomes: "", next_steps: "",
    kpi: "", kpi_target: "", kpi_actual: "", kpi_delta_pct: "",
  };
}

function toPayload(f) {
  const milestones = (f.milestones || "").split("\n").map((l) => l.trim()).filter(Boolean)
    .map((l) => ({ name: l.replace(/\s*\[x\]\s*$/i, ""), done: /\[x\]\s*$/i.test(l) }));
  const kpi_performance = f.kpi ? [{ kpi: f.kpi, target: f.kpi_target, actual: f.kpi_actual, delta_pct: f.kpi_delta_pct }] : [];
  const expected_kpis = f.expected_kpi ? [{ kpi: f.expected_kpi, target: f.expected_target }] : [];
  return {
    title: f.title, category: f.category, business_area: f.business_area, owner: f.owner, status: f.status,
    source: f.source || "Manual", recommendation_id: f.recommendation_id || null,
    confidence: f.confidence ? parseInt(f.confidence, 10) : null, priority: f.priority || null,
    recommended_timeline: f.recommended_timeline || null, related_eii: f.related_eii ? parseInt(f.related_eii, 10) : null,
    recommendation_summary: f.recommendation_summary || "",
    related_kpis: (f.related_kpis || "").split(",").map((s) => s.trim()).filter(Boolean),
    strategic_context: { challenge: f.challenge, opportunity: f.opportunity, goals: f.goals, assumptions: f.assumptions, risks: f.risks, alternatives: f.alternatives, rationale: f.rationale },
    execution: { action_plan: f.action_plan, budget: f.budget, timeline: f.timeline, milestones, dependencies: f.dependencies },
    success_metrics: { expected_kpis, target_results: f.target_results, review_date: f.review_date, success_criteria: f.success_criteria },
    outcome_review: { actual_results: f.actual_results, revenue_impact: f.revenue_impact, lessons_learned: f.lessons_learned, unexpected_outcomes: f.unexpected_outcomes, next_steps: f.next_steps, kpi_performance },
  };
}

function fromDoc(d) {
  const sc = d.strategic_context || {}, ex = d.execution || {}, sm = d.success_metrics || {}, orv = d.outcome_review || {};
  const kp = (orv.kpi_performance || [])[0] || {};
  const ek = (sm.expected_kpis || [])[0] || {};
  return {
    title: d.title || "", category: d.category || "General", business_area: d.business_area || "General",
    owner: d.owner || "Angella", status: d.status || "Idea",
    source: d.source || "Manual", recommendation_id: d.recommendation_id || "", confidence: d.confidence || "",
    priority: d.priority || "", recommended_timeline: d.recommended_timeline || "", related_eii: d.related_eii || "",
    recommendation_summary: d.recommendation_summary || "", related_kpis: (d.related_kpis || []).join(", "),
    challenge: sc.challenge || "", opportunity: sc.opportunity || "", goals: sc.goals || "", assumptions: sc.assumptions || "",
    risks: sc.risks || "", alternatives: sc.alternatives || "", rationale: sc.rationale || "",
    action_plan: ex.action_plan || "", budget: ex.budget || "", timeline: ex.timeline || "",
    milestones: (ex.milestones || []).map((m) => `${m.name}${m.done ? " [x]" : ""}`).join("\n"), dependencies: ex.dependencies || "",
    expected_kpi: ek.kpi || "", expected_target: ek.target || "",
    target_results: sm.target_results || "", review_date: sm.review_date || "", success_criteria: sm.success_criteria || "",
    actual_results: orv.actual_results || "", revenue_impact: orv.revenue_impact || "", lessons_learned: orv.lessons_learned || "",
    unexpected_outcomes: orv.unexpected_outcomes || "", next_steps: orv.next_steps || "",
    kpi: kp.kpi || "", kpi_target: kp.target || "", kpi_actual: kp.actual || "", kpi_delta_pct: kp.delta_pct || "",
  };
}

export default function DecisionsPanel({ initialDraft, onDraftConsumed }) {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ q: "", status: "", category: "", business_area: "" });
  const [editing, setEditing] = useState(null);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const [s, l] = await Promise.all([
      api.get("/decisions/analytics/summary"),
      api.get("/decisions", { params }),
    ]);
    setSummary(s.data);
    setItems(l.data.items);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (initialDraft) { setEditId(null); setEditing(initialDraft); onDraftConsumed?.(); }
  }, [initialDraft, onDraftConsumed]);

  const openNew = () => { setEditId(null); setEditing(emptyForm()); };
  const openEdit = async (id) => { const { data } = await api.get(`/decisions/${id}`); setDetail(null); setEditId(id); setEditing(fromDoc(data)); };
  const openDetail = async (id) => { const { data } = await api.get(`/decisions/${id}`); setDetail(data); };

  const save = async () => {
    if (!editing.title.trim()) return;
    const payload = toPayload(editing);
    if (editId) await api.put(`/decisions/${editId}`, payload);
    else await api.post("/decisions", payload);
    setEditing(null); setEditId(null);
    load();
  };

  if (!summary) return <div className="empty-state">Loading decisions…</div>;
  const v = summary.strategic_velocity || {};

  return (
    <>
      <div className="metric-grid">
        <div className="metric-card"><div className="mc-label">Success rate</div><div className="mc-value" style={{ color: "#1D9E75" }}>{summary.average_success_rate}%</div><div className="mc-delta up">{summary.completed_count} completed</div></div>
        <div className="metric-card"><div className="mc-label">Created this month</div><div className="mc-value">{summary.created_this_month}</div></div>
        <div className="metric-card"><div className="mc-label">Completed</div><div className="mc-value">{summary.completed_count}</div></div>
        <div className="metric-card"><div className="mc-label">Strategic velocity</div><div className="mc-value">{v.last_30_days}</div><div className={`mc-delta ${v.trend_pct >= 0 ? "up" : "down"}`}>{v.trend_pct >= 0 ? "+" : ""}{v.trend_pct}% /30d</div></div>
        <div className="metric-card"><div className="mc-label">Active initiatives</div><div className="mc-value">{summary.active_initiatives.length}</div></div>
        <div className="metric-card"><div className="mc-label">Awaiting review</div><div className="mc-value" style={{ color: "#8A6100" }}>{summary.awaiting_review.length}</div></div>
      </div>

      <div className="toolbar">
        <input className="filter-input" placeholder="Search decisions…" value={filters.q} data-testid="decisions-search" onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        <select className="filter-select" value={filters.status} data-testid="decisions-filter-status" onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>{LIFECYCLE.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select className="filter-select" value={filters.business_area} onChange={(e) => setFilters({ ...filters, business_area: e.target.value })}>
          <option value="">All areas</option>{AREAS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <div className="spacer" />
        <button className="btn-primary" onClick={openNew} data-testid="new-decision-button"><Plus size={14} /> New decision</button>
      </div>

      <div className="two-col">
        <div>
          <SectionLabel>Active strategic initiatives</SectionLabel>
          <div className="card">
            {summary.active_initiatives.length === 0 && <div className="empty-state">No active initiatives.</div>}
            {summary.active_initiatives.map((it) => (
              <div className="decision-item" key={it.id} onClick={() => openDetail(it.id)}>
                <div style={{ flex: 1 }}><div className="decision-title">{it.title}</div><div className="decision-meta">{it.status} · {it.business_area}</div></div>
                <div className="mini-progress"><div style={{ width: `${it.progress}%` }} /></div>
                <span className="decision-meta">{it.progress}%</span>
              </div>
            ))}
          </div>
          <SectionLabel>Top performing initiatives</SectionLabel>
          <div className="card">
            {summary.top_performing.length === 0 && <div className="empty-state">No completed outcomes yet.</div>}
            {summary.top_performing.map((t) => (
              <div className="decision-item" key={t.id} onClick={() => openDetail(t.id)}>
                <TrendingUp size={15} color="#1D9E75" />
                <div className="decision-title" style={{ flex: 1, fontWeight: 400 }}>{t.title}</div>
                <span className="decision-meta" style={{ color: "#1D9E75", fontWeight: 500 }}>{t.success_pct}%{t.roi_pct != null ? ` · ROI ${t.roi_pct}%` : ""}</span>
              </div>
            ))}
          </div>
          <SectionLabel>Upcoming review dates</SectionLabel>
          <div className="card">
            {summary.upcoming_reviews.length === 0 && <div className="empty-state">No upcoming reviews.</div>}
            {summary.upcoming_reviews.map((r) => (
              <div className="decision-item" key={r.id} onClick={() => openDetail(r.id)}>
                <Clock size={15} color="#1F75A8" /><div className="decision-title" style={{ flex: 1, fontWeight: 400 }}>{r.title}</div>
                <span className="decision-meta">{(r.review_date || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <SectionLabel>Awaiting approval</SectionLabel>
          <div className="card">
            {summary.awaiting_approval.length === 0 && <div className="empty-state">Nothing pending.</div>}
            {summary.awaiting_approval.map((r) => (
              <div className="decision-item" key={r.id} onClick={() => openDetail(r.id)}>
                <ClipboardList size={15} color="#3C3489" /><div className="decision-title" style={{ flex: 1, fontWeight: 400 }}>{r.title}</div>
                <span className="decision-meta">{r.category}</span>
              </div>
            ))}
          </div>
          <SectionLabel>Awaiting review</SectionLabel>
          <div className="card">
            {summary.awaiting_review.length === 0 && <div className="empty-state">Nothing to review.</div>}
            {summary.awaiting_review.map((r) => (
              <div className="decision-item" key={r.id} onClick={() => openDetail(r.id)}>
                <Target size={15} color="#8A6100" /><div className="decision-title" style={{ flex: 1, fontWeight: 400 }}>{r.title}</div>
                <span className="decision-meta">{r.business_area}</span>
              </div>
            ))}
          </div>
          <SectionLabel>Decisions by business area</SectionLabel>
          <div className="card">
            <SourceBars rows={summary.by_business_area.map((b, i) => ({ name: b.name, value: b.value, color: ["#1F75A8", "#2F7EA8", "#5DCAA5", "#B8975A", "#888780", "#7F77DD", "#B5D4F4"][i % 7] }))} />
          </div>
        </div>
      </div>

      <SectionLabel>Executive decision timeline</SectionLabel>
      <div className="card" data-testid="decisions-list">
        {items.length === 0 && <div className="empty-state">No decisions match your filters.</div>}
        {items.map((it) => (
          <div className="decision-item" key={it.id} onClick={() => openDetail(it.id)}>
            <div className="timeline-dot" />
            <div style={{ flex: 1 }}>
              <div className="decision-title">
                {it.title}
                {it.source === "Executive Advisor" && <span className="badge-prio prio-Medium" style={{ marginLeft: 6, background: "#EEEDFE", color: "#3C3489" }}>Advisor</span>}
              </div>
              <div className="decision-meta">{it.category} · {it.business_area} · {(it.date_created || "").slice(0, 10)} · v{it.version}{it.outcome ? ` · success ${it.outcome.success_pct}%` : ""}</div>
            </div>
            <span className={stClass(it.status)}>{it.status}</span>
          </div>
        ))}
      </div>

      {editing && <DecisionForm form={editing} setForm={setEditing} onClose={() => { setEditing(null); setEditId(null); }} onSave={save} isEdit={!!editId} />}
      {detail && <DecisionDetail doc={detail} onClose={() => setDetail(null)} onEdit={() => openEdit(detail.id)} />}
    </>
  );
}

function Field({ label, children }) {
  return <div className="form-group"><label className="form-label">{label}</label>{children}</div>;
}

function DecisionForm({ form, setForm, onClose, onSave, isEdit }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ta = (k, label) => <Field label={label}><textarea className="form-textarea" value={form[k]} onChange={set(k)} /></Field>;
  const fromAdvisor = form.source === "Executive Advisor";
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()} data-testid="decision-form">
        <div className="modal-head">
          <div className="modal-title">{isEdit ? "Edit decision" : (fromAdvisor ? "Promote to strategic decision" : "New strategic decision")}</div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        {fromAdvisor && (
          <div className="advisor-context" data-testid="advisor-context-block">
            <div className="ac-row">
              <span><b>Source:</b> Executive Advisor</span>
              {form.confidence && <span><b>Confidence:</b> {form.confidence}%</span>}
              {form.priority && <span><b>Priority:</b> {form.priority}</span>}
              {form.recommended_timeline && <span><b>Timeline:</b> {form.recommended_timeline}</span>}
              {form.related_eii && <span><b>EII at recommendation:</b> {form.related_eii}</span>}
            </div>
            {form.related_kpis && <div className="ac-row" style={{ marginTop: 6 }}><span><b>Related KPIs:</b> {form.related_kpis}</span></div>}
          </div>
        )}

        <Field label="Decision title"><input className="form-input" value={form.title} onChange={set("title")} data-testid="decision-title-input" /></Field>
        {fromAdvisor && ta("recommendation_summary", "Recommendation summary")}
        <div className="form-row">
          <Field label="Category"><select className="form-select" value={form.category} onChange={set("category")}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
          <Field label="Business area"><select className="form-select" value={form.business_area} onChange={set("business_area")}>{AREAS.map((c) => <option key={c}>{c}</option>)}</select></Field>
        </div>
        <div className="form-row">
          <Field label="Owner"><input className="form-input" value={form.owner} onChange={set("owner")} /></Field>
          <Field label="Status"><select className="form-select" value={form.status} onChange={set("status")} data-testid="decision-status-select">{LIFECYCLE.map((c) => <option key={c}>{c}</option>)}</select></Field>
        </div>

        <div className="form-section-title">Strategic context</div>
        {ta("challenge", "Business challenge")}
        {ta("opportunity", "Opportunity")}
        {ta("goals", "Goals")}
        {ta("risks", "Risks")}
        {ta("alternatives", "Alternatives considered")}
        {ta("rationale", "Why this decision was chosen")}

        <div className="form-section-title">Execution</div>
        {ta("action_plan", "Action plan")}
        <div className="form-row">
          <Field label="Budget"><input className="form-input" value={form.budget} onChange={set("budget")} placeholder="e.g. $800" /></Field>
          <Field label="Timeline"><input className="form-input" value={form.timeline} onChange={set("timeline")} /></Field>
        </div>
        {ta("milestones", "Milestones (one per line, add [x] when done)")}
        {ta("dependencies", "Dependencies")}

        <div className="form-section-title">Success metrics (expected)</div>
        <div className="form-row">
          <Field label="Expected KPI"><input className="form-input" value={form.expected_kpi} onChange={set("expected_kpi")} /></Field>
          <Field label="Target"><input className="form-input" value={form.expected_target} onChange={set("expected_target")} /></Field>
        </div>
        <div className="form-row">
          <Field label="Review date"><input type="date" className="form-input" value={form.review_date} onChange={set("review_date")} /></Field>
          <Field label="Success criteria"><input className="form-input" value={form.success_criteria} onChange={set("success_criteria")} /></Field>
        </div>

        <div className="form-section-title">Outcome review (closed-loop)</div>
        <div className="form-row">
          <Field label="Actual KPI"><input className="form-input" value={form.kpi} onChange={set("kpi")} /></Field>
          <Field label="Target"><input className="form-input" value={form.kpi_target} onChange={set("kpi_target")} /></Field>
        </div>
        <div className="form-row">
          <Field label="Actual"><input className="form-input" value={form.kpi_actual} onChange={set("kpi_actual")} /></Field>
          <Field label="Delta % (+/-)"><input className="form-input" value={form.kpi_delta_pct} onChange={set("kpi_delta_pct")} placeholder="e.g. 14" /></Field>
        </div>
        <Field label="Revenue impact ($ gain, for ROI)"><input className="form-input" value={form.revenue_impact} onChange={set("revenue_impact")} placeholder="e.g. $4,320" /></Field>
        {ta("actual_results", "Actual results")}
        {ta("lessons_learned", "Lessons learned")}
        {ta("next_steps", "Recommended next steps")}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button className="btn-primary" onClick={onSave} data-testid="decision-save-button">{isEdit ? "Save changes" : (fromAdvisor ? "Create decision" : "Create decision")}</button>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function DBlock({ label, value }) {
  if (!value) return null;
  return <div className="detail-block"><div className="detail-label">{label}</div><div className="detail-value">{value}</div></div>;
}

const ICON = { sparkles: Sparkles, flag: Flag, git: GitBranch, check: CheckIcon, target: Target, archive: Archive, dot: CheckCircle2 };

function DecisionDetail({ doc, onClose, onEdit }) {
  const sc = doc.strategic_context || {}, ex = doc.execution || {}, sm = doc.success_metrics || {}, orv = doc.outcome_review || {};
  const oc = doc.outcome;
  const [timeline, setTimeline] = useState(null);

  useEffect(() => { api.get(`/decisions/${doc.id}/timeline`).then(({ data }) => setTimeline(data)); }, [doc.id]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()} data-testid="decision-detail">
        <div className="modal-head">
          <div>
            <div className="modal-title">{doc.title}</div>
            <div className="decision-meta" style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span className={stClass(doc.status)}>{doc.status}</span>
              {doc.priority && <span className={`badge-prio prio-${doc.priority}`}>{doc.priority}</span>}
              <span>{doc.category} · {doc.business_area} · {doc.owner || "—"} · v{doc.version}</span>
              {doc.source === "Executive Advisor" && <span style={{ color: "#3C3489", display: "inline-flex", alignItems: "center", gap: 4 }}><Sparkles size={12} /> Advisor</span>}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        {oc && (
          <>
            <div className="form-section-title" style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}>Closed-loop outcome</div>
            <div className="outcome-grid" data-testid="decision-outcome">
              <div className="outcome-card"><div className="outcome-label">Success</div><div className="outcome-value">{oc.success_pct != null ? `${oc.success_pct}%` : "—"}</div></div>
              <div className="outcome-card"><div className="outcome-label">ROI</div><div className="outcome-value">{oc.roi_pct != null ? `${oc.roi_pct}%` : "—"}</div></div>
              <div className="outcome-card"><div className="outcome-label">Goal</div><div className="outcome-value" style={{ fontSize: 14 }}>{oc.goal_achievement}</div></div>
              <div className="outcome-card"><div className="outcome-label">Time to complete</div><div className="outcome-value" style={{ fontSize: 15 }}>{oc.time_to_completion_days != null ? `${oc.time_to_completion_days}d` : "—"}</div></div>
            </div>
          </>
        )}

        <div className="card" style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 12 }}>
          <TrendingUp size={18} color="#1F75A8" />
          <div style={{ flex: 1 }}><div className="detail-label">Progress</div><div className="mini-progress" style={{ width: "100%", marginTop: 4 }}><div style={{ width: `${doc.progress || 0}%` }} /></div></div>
          <span className="decision-meta">{doc.progress || 0}%</span>
        </div>

        {doc.recommendation_summary && <><div className="form-section-title">Recommendation</div><DBlock label="Summary" value={doc.recommendation_summary} /></>}

        <div className="form-section-title">Strategic context</div>
        <DBlock label="Business challenge" value={sc.challenge} />
        <DBlock label="Opportunity" value={sc.opportunity} />
        <DBlock label="Goals" value={sc.goals} />
        <DBlock label="Risks" value={sc.risks} />
        <DBlock label="Alternatives considered" value={sc.alternatives} />
        <DBlock label="Why chosen" value={sc.rationale} />

        <div className="form-section-title">Execution</div>
        <DBlock label="Action plan" value={ex.action_plan} />
        <DBlock label="Budget" value={ex.budget} />
        <DBlock label="Timeline" value={ex.timeline} />
        {(ex.milestones || []).length > 0 && (
          <div className="detail-block"><div className="detail-label">Milestones</div>
            {ex.milestones.map((m, i) => <div className="detail-value" key={i}>{m.done ? "✓" : "○"} {m.name}</div>)}
          </div>
        )}

        <div className="form-section-title">Success metrics</div>
        {(sm.expected_kpis || []).map((k, i) => <DBlock key={i} label="Expected KPI" value={`${k.kpi}: target ${k.target}`} />)}
        <DBlock label="Review date" value={(sm.review_date || "").slice(0, 10)} />
        <DBlock label="Success criteria" value={sm.success_criteria} />

        {(orv.actual_results || orv.lessons_learned || (orv.kpi_performance || []).length > 0) && (
          <>
            <div className="form-section-title">Outcome review</div>
            {(orv.kpi_performance || []).map((k, i) => <DBlock key={i} label="KPI performance" value={`${k.kpi}: target ${k.target}, actual ${k.actual} (${k.delta_pct >= 0 ? "+" : ""}${k.delta_pct}%)`} />)}
            <DBlock label="Revenue impact" value={orv.revenue_impact} />
            <DBlock label="Actual results" value={orv.actual_results} />
            <DBlock label="Lessons learned" value={orv.lessons_learned} />
            <DBlock label="Recommended next steps" value={orv.next_steps} />
          </>
        )}

        <div className="form-section-title">Executive lifecycle timeline</div>
        <div className="lc-timeline" data-testid="decision-timeline">
          {!timeline && <div className="empty-state">Loading…</div>}
          {timeline && timeline.events.map((e, i) => {
            const Ico = ICON[e.icon] || CheckCircle2;
            return (
              <div className={`lc-event ${e.done ? "done" : ""}`} key={i}>
                <div className="lc-stage"><Ico size={12} style={{ marginRight: 5, verticalAlign: "middle" }} />{e.stage}</div>
                <div className="lc-label">{e.label}</div>
                {e.at && <div className="lc-at">{new Date(e.at).toLocaleString()}</div>}
              </div>
            );
          })}
        </div>
        {timeline && (
          <div className="info-note" style={{ marginTop: 10 }}>
            recommendation_id: {timeline.recommendation_id || "—"} · decision_id: {timeline.decision_id.slice(0, 8)} · outcome_id: {timeline.outcome_id ? timeline.outcome_id.slice(0, 8) : "—"}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button className="btn-primary" onClick={onEdit} data-testid="decision-edit-button">Edit / advance stage</button>
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export { emptyForm };
